// Core
import { computed } from "vue";

// Services & Helpers
import { EXCLUDED_PLAN_IDS } from "@/content/subscription";

// Stores
import { useTeamStore } from "@/store/team";
import { useAdminStore } from "@/store/admin";

// Types
import type { SubscriptionT } from "@/@types/subscription";

type CreateSubscriptionPayloadT = { teamId: string; planId: string };

type CreateSubscriptionTrialPayloadT = { trialEnd?: string; teamId: string; planId: string };

type UpdateSubscriptionPayloadT = {
  subscriptionId: string;
  planId?: string;
  status?: string;
};

export const useSubscription = () => {
  const teamStore = useTeamStore();
  const adminStore = useAdminStore();

  const filterSubscriptions = <T extends SubscriptionT>(subscriptions: T[], predicate: (sub: T) => boolean): T[] => {
    return subscriptions.filter(predicate);
  };

  const findMainSubscription = (subscriptions: SubscriptionT[]): SubscriptionT | null => {
    let filteredSubscriptions: SubscriptionT[] = subscriptions;

    if (subscriptions.some(sub => sub.status === "active")) {
      filteredSubscriptions = subscriptions.filter(sub => sub.status === "active");
    }

    const mainSub = filteredSubscriptions.find(sub => !EXCLUDED_PLAN_IDS.includes(sub.planId));

    return mainSub || subscriptions[0] || null;
  };

  const createSubscription = async (payload: CreateSubscriptionPayloadT) => {
    await adminStore.createSubscription({
      planId: payload.planId,
      teamId: payload.teamId
    });
  };

  const createTrial = async (payload: CreateSubscriptionTrialPayloadT) => {
    await adminStore.createSubscription({
      planId: payload.planId,
      teamId: payload.teamId,
      trialEnd: payload.trialEnd
    });
  };

  const updateSubscription = async (payload: UpdateSubscriptionPayloadT) => {
    const updateData = {
      ...(payload.planId !== undefined && { planId: payload.planId }),
      ...(payload.status !== undefined && { status: payload.status })
    };

    await adminStore.updateSubscription(payload.subscriptionId, updateData);
  };

  const main = computed<SubscriptionT | null>(() => findMainSubscription(teamStore.subscriptions));

  const others = computed<SubscriptionT[]>(() => teamStore.subscriptions.filter(sub => sub.id !== main.value?.id));

  const monthly = computed<SubscriptionT[]>(() =>
    filterSubscriptions(
      [...(main.value?.interval === "monthly" ? [main.value] : []), ...others.value],
      sub => sub.interval === "monthly"
    )
  );

  const annual = computed<SubscriptionT[]>(() =>
    filterSubscriptions(
      [...(main.value?.interval === "annual" ? [main.value] : []), ...others.value],
      sub => sub.interval === "annual"
    )
  );

  const active = computed<SubscriptionT[]>(() =>
    filterSubscriptions(
      teamStore.subscriptions,
      sub =>
        !sub.cancel?.cancelRequestedAt && !sub.cancel?.cancelAtCycleEnd && ["active", "overdue"].includes(sub.status)
    )
  );

  const isLitePlan = computed<boolean>(() => main.value?.planId === "BWP4");

  return {
    main,
    others,
    monthly,
    annual,
    active,
    isLitePlan,
    createSubscription,
    createTrial,
    updateSubscription
  };
};
